/**
 * React glue over the vanilla `LiveStore` (see `createLiveStore.ts`).
 *
 * `useSyncExternalStore` is built into React 18 — import it directly from
 * `react` (no `use-sync-external-store` shim needed).
 *
 * Each row subscribes to a single key (`useLiveState`), so a WS frame that
 * changes one pair re-renders only that row. The tab subscribes to the
 * throttled order list (`useLiveOrder`) for the virtualizer.
 */
import { useSyncExternalStore } from 'react';
import type { LiveStore } from './createLiveStore';

/**
 * Subscribe one row to its pair's live state. Returns a referentially stable
 * value between real changes (the store caches per-key snapshots), so this is
 * safe against `useSyncExternalStore` tearing.
 *
 * Do NOT "optimize" the `subscribe` argument into a `useCallback([store, id])`.
 * The inline arrow re-subscribes on every render, and that is load-bearing:
 * `applySnapshot` evicts a key by deleting its whole entry — listeners Set
 * included — while the row is still mounted, so a re-added key would otherwise
 * keep a listener React never re-registers and the row would silently freeze.
 * The textbook memoized-subscribe pattern makes that orphan permanent.
 */
export function useLiveState<T>(
  store: LiveStore<T>,
  id: string
): T | undefined {
  return useSyncExternalStore(
    cb => store.subscribeKey(id, cb),
    () => store.getKeySnapshot(id)
  );
}

/**
 * Subscribe the tab to the throttled, stable sorted id list. Recomputes at
 * most once per `ORDER_THROTTLE_MS`, rAF-coalesced; returns the same array
 * reference when the order is unchanged.
 */
export function useLiveOrder<T>(store: LiveStore<T>): string[] {
  return useSyncExternalStore(store.subscribeOrder, store.getOrderSnapshot);
}
